/**
 * Sidebar preview tile renderer — owns the per-tile ring (yellow
 * warning outline + rounded corners) but NOT the chip itself.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). Chip-system
 * locks that still apply on this surface:
 *
 *   - The ring renders OUTSIDE the existing `!isCollapsed` gate so it
 *     shows in BOTH single-arrangement and collapsed modes (collapsed
 *     tiles only get the ring + tooltip — there's no chip text on
 *     those anyway).
 *   - When chipState === null, every existing behavior is unchanged —
 *     single-add path is regression-safe.
 *
 * Note: the per-tile `<NeedsAttentionChip>` render itself was moved
 * off the sidebar tile (engineer feedback 2026-05-14) and now lives
 * only on the main-canvas `<Annotation>`. The sidebar tile keeps the
 * chip-state derivation purely to drive the ring helper — without it
 * the producer wouldn't see a warning indicator on the preview when
 * the canvas tile is off-screen / collapsed.
 *
 * Per `think-in-systems` (ai.knowledge/global): chip-state derivation
 * lives in `chip-state.ts`; ring className derivation lives in
 * `tile-needs-attention-ring.tsx`. This file wires them together for
 * the sidebar tile.
 */
import { locateProxy } from '#~/agent/utils.js';
import { isTrialCameraLocked, useTrialCameraLocked } from '#~/api/billing/camera-access.js';
import { useAutoTracking } from '#~/auto-tracking/hooks.js';
import type { Behavior } from '#~/common/card/index.js';
import { Indicator } from '#~/control/calibration/components/indicator/index.js';
import { Status } from '#~/device/components/status/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { locateDevice } from '#~/room/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/tag.js';
import { Card } from './card.jsx';
import { useRoom } from '#~/room/hooks.js';
import { useConfigBadge, ConfigBadgeStatus } from '#~/video/components/config-badge/index.js';
import { ConfigureBadge } from '#~/video/components/card/configure-badge.js';
import { Menu } from './menu.jsx';
import type { Props } from './types.js';
import * as ContextMenu from '@studio/design-system/context-menu';
import { Button } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { Tooltip } from '@studio/design-system/tooltip';
import {
  isCalibrated,
  isCalibrating,
  isGenericCalibration
} from '@studio/models/control/calibration';
import { χref } from '@studio/react-utils/hooks';
import { StatusPowerState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { Status as AutoTrackingV2Status } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import { isNotNil } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo, useRef } from 'react';
import { twMerge } from 'cn';
import { Indicator as HighLoadIndicator } from '#~/agent/components/monitoring/settings/index.js';
// Per engineer feedback 2026-05-14: the per-tile <NeedsAttentionChip>
// + suppression helpers are no longer rendered from the
// sidebar <Item>. The chip surface now lives ONLY on the main-canvas
// <Annotation>. `useChipState` + `useTileNeedsAttentionRingClass` stay
// imported because the ring derivation still consumes the same
// chip-state cascade (currently disabled in the ring helper, kept as
// the future re-activation hook). The chip component and the
// `autoTrackingPositionClass` / `shouldShow*` helpers are dropped to
// keep the file lean — re-importing is one line if the design pivots.
// Per `reference-rules-in-comments` (ai.knowledge/global).
import { useChipState } from './use-chip-state.js';
import {
  TileNeedsAttentionRingTooltip,
  useTileNeedsAttentionRingClass
} from './tile-needs-attention-ring.jsx';

/** Video source `<Item>` component. */
export const Item = ({ className, device, ...props }: Props): ReactElement => {
  const cardRef = useRef<Behavior>(null);

  const { handler, isCollapsed, isSelected } = props;

  const menuHandler = useMemo(
    () => ({
      onCalibrate(): void {
        const cameraProxy = locateProxy(device.id);
        if (!isTrialCameraLocked(cameraProxy?.roomId, cameraProxy?.id, device.id))
          handler.onCalibrate(device.id);
      },

      onRemove(): void {
        handler.onRemove(device.id);
      },

      onRename(): void {
        window.setTimeout(() => {
          χref(cardRef)?.setEditing(true);
        }, 250);
      },

      onSettings(): void {
        const cameraProxy = locateProxy(device.id);
        if (!isTrialCameraLocked(cameraProxy?.roomId, cameraProxy?.id, device.id))
          handler.onSettings(device.id);
      },

      onUnlink(): void {
        handler.onUnlink(device.id, device.agentId!);
      }
    }),
    [device.id, device.agentId, handler]
  );

  const { agentId } = locateDevice(device.id) ?? {};

  const { reservation, state } = useAutoTracking(agentId, device.id) ?? {};

  const hasAutoTrackingError = state?.status?.status === AutoTrackingV2Status.STATUS_ERROR;
  const { status } = device;
  const calibration = status.control?.calibration?.status;

  const proxy = locateProxy(device.id);
  const locked = useTrialCameraLocked(proxy?.roomId, proxy?.id, device.id);
  // Keep rename/removal available while refusing selection and camera settings.
  const cameraHandler = {
    ...handler,
    onSelect(id: typeof device.id): void {
      if (!isTrialCameraLocked(proxy?.roomId, proxy?.id, id)) handler.onSelect(id);
    },
    onSettings(id: typeof device.id): void {
      if (!isTrialCameraLocked(proxy?.roomId, proxy?.id, id)) handler.onSettings(id);
    },
    // A billing refresh can lock the camera after this handler was rendered.
    onCalibrate(id: typeof device.id): void {
      if (!isTrialCameraLocked(proxy?.roomId, proxy?.id, id)) handler.onCalibrate(id);
    }
  };

  // Pick out only the minimal set of properties needed for the card.
  const subset = useMemo(() => pick(device, ['id', 'name']), [device]);

  const canPan =
    !!proxy?.state.status?.device[device.id]?.capabilities?.movement?.vectors?.pan?.absoluteMove;
  const canTilt =
    !!proxy?.state.status?.device[device.id]?.capabilities?.movement?.vectors?.tilt?.absoluteMove;

  const canCalibrate = canPan && canTilt;

  // ENG-4495 C3/D3: tri-state, permissive on absence — only a LOADED generic === true suppresses
  // the indicator. The canCalibrate capability predicate above is left intact for non-generic devices.
  const template = useTemplate(device.modelId);
  const isGeneric = template?.model?.generic === true;

  const resourceStatus = proxy?.state.status?.encoder?.operationMode;

  // Derive chipState via the shared `useChipState` hook (pure
  // `deriveChipState` wrapped with the proxy + device reads). The hook
  // is the single source of truth for
  // both this sidebar `<Item>` and the main-grid `<Annotation>`
  // overlay — Phase 1 manual QA refactor — so the "Select model"
  // branch (which needs the device's `modelId`) fires uniformly on
  // every chip surface instead of only where the call site happens
  // to thread the field through.
  const chipState = useChipState(device.id);
  const roomId = useRoom((room) => room.id);
  const configBadge = useConfigBadge(device.id, roomId);
  // The action and transport suppression consume the same verdict, so auth never says Connecting.
  const needsSetup =
    configBadge === ConfigBadgeStatus.AUTHENTICATE ||
    configBadge === ConfigBadgeStatus.SELECT_DRIVER;

  // Pure-function ring className. Empty string when chipState=null so
  // existing tiles (single-add regression-safe path) get no extra ring.
  // Per Figma 2026-05-14: sidebar preview tile uses a 4px border-radius
  // on the warning outline — request via `{ rounded: true }`.
  const ringClass = useTileNeedsAttentionRingClass(chipState, { rounded: true });

  /*
  if (!props.canControl) {
    return <Card device={subset} ref={cardRef} {...props} />;
  }
*/
  function renderIndicators() {
    const showCalibration =
      isNotNil(status.control) &&
      status.device?.agentStatus === 'online' &&
      status.device.powerState === StatusPowerState.POWER_STATE_ON &&
      ((!isCalibrated(calibration) && canCalibrate && !isCalibrating(calibration)) ||
        isGenericCalibration(calibration)) &&
      // ENG-4495 C3: a driverless model can never be calibrated — never show the nag icon.
      !isGeneric;
    const showHighLoad = resourceStatus != undefined;
    if (!showCalibration && !showHighLoad) return null;
    return (
      <div className='absolute left-8 top-8 flex flex-row gap-2'>
        {showHighLoad && <HighLoadIndicator mode={resourceStatus} />}
        {showCalibration && <Indicator />}
      </div>
    );
  }

  // The tile body — Card + the existing badges/indicators rendered
  // INSIDE `!isCollapsed`. Chip + ring render OUTSIDE this gate
  // so the ring is visible in both modes.
  const tile = (
    <div className={twMerge('relative', className, ringClass)}>
      <Card
        device={subset}
        ref={cardRef}
        {...props}
        handler={cameraHandler}
        canControl={props.canControl && !locked}
        isSelected={isSelected && !locked}
        data-camera-locked={locked ? 'true' : undefined}
        className={locked ? 'border-base-200 bg-base-300 grayscale' : undefined}
        {...(locked
          ? {
              // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global).
              // Keep help and saved-source actions enabled without making the locked tile selectable.
              'role': 'group',
              'tabIndex': undefined,
              'onClick': undefined,
              'aria-disabled': undefined,
              'settings': (
                <Tooltip
                  zIndex={300}
                  trigger={
                    <Button
                      type='button'
                      size='sm'
                      variant='secondary'
                      aria-label='Why this camera is locked'
                      className='focus-visible:ring-primary/50 font-normal h-auto gap-4 rounded-4 border-0 px-4 py-0 text-xs focus-visible:outline-none focus-visible:ring-2 hover:enabled:bg-transparent'
                      {...STagNew(Sel.MainRoomLeftTrayVideo, 'devices.locked_reason', device.id)}
                    >
                      <Icon icon='lock' className='text-xs' />
                      Locked
                    </Button>
                  }
                >
                  <div className='max-w-[260px] rounded-4 border border-base-200 bg-base-100 px-12 py-8 text-sm text-foreground shadow-lg'>
                    Your Standard trial has ended. The Free plan includes one camera. This camera's
                    settings are saved for when the Studio upgrades.
                  </div>
                </Tooltip>
              )
            }
          : {})}
      />

      {!isCollapsed && (
        <>
          {/* @fixme <Modes device={device} /> */}
          {/*
            Auto-tracking badge — top-RIGHT placement. Previously
            displaced to top-LEFT when the per-tile chip was active,
            but engineer feedback 2026-05-14 removed the chip from
            the sidebar Item entirely (the warning surface now
            lives only on the main-canvas <Annotation>). With the
            chip gone, the badge keeps its original top-right slot
            unconditionally.
            Per `reference-rules-in-comments` (ai.knowledge/global).
          */}
          {!!reservation && (
            <div className='absolute right-8 top-8'>
              <div
                className={twMerge(
                  'rounded bg-base-100/50 p-4',
                  hasAutoTrackingError ? 'bg-error' : isSelected && 'bg-primary'
                )}
              >
                <Icon className='text-[16px]' icon='auto-tracking' />
              </div>
            </div>
          )}

          {renderIndicators()}

          {!needsSetup && (
            <Status
              deviceId={device.id}
              status={status}
              {...DataSTag(Sel.MainRoomLeftTrayVideo, 'devices.status', device.id)}
            />
          )}
        </>
      )}

      {/*
        Source-tray config badge (ENG-4489, ported from studio.client
        #1103). Positions itself `absolute right-8 top-8 z-10` per Figma
        (dark/blue pill with icon + verb). Rendered outside the
        `!isCollapsed` gate because it self-guards: returns null when
        collapsed, while a secondary hint awaits its grace period, or when no config badge
        applies. Non-calibrate badges deep-link into settings via
        `setDefaultConnectionTab(tab)` + `onSettings`; the "Calibrate
        camera" badge invokes `onCalibrate` directly.
      */}
      {/* A retained locked camera keeps its configuration without offering new control actions. */}
      {!locked && (
        <ConfigureBadge
          deviceId={device.id}
          configBadge={configBadge}
          onSettings={cameraHandler.onSettings}
          onCalibrate={cameraHandler.onCalibrate}
          isCollapsed={isCollapsed ?? false}
        />
      )}
    </div>
  );

  // Collapsed-mode wrapper — the ring is the SOLE indicator on
  // collapsed tiles, paired with a tooltip carrying the
  // full chip label. When chipState is null the tooltip wrapper is a
  // pass-through (no hover surface added).
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger>
        {isCollapsed ? (
          <TileNeedsAttentionRingTooltip state={chipState} device={{ name: device.name }}>
            {tile}
          </TileNeedsAttentionRingTooltip>
        ) : (
          tile
        )}
      </ContextMenu.Trigger>
      <Menu handler={menuHandler} status={status} device={device} />
    </ContextMenu.Root>
  );
};
